import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Box,
  Button,
  Card,
  CardContent,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  MenuItem,
  Typography,
  IconButton,
  Chip,
  Grid,
  Alert,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  List,
  ListItem,
  ListItemText,
  Divider,
  Tabs,
  Tab,
  CardActionArea
} from '@mui/material';
import {
  Add as AddIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
  ExpandMore as ExpandMoreIcon,
  Computer as ComputerIcon,
  Apps as AppsIcon,
  Memory as MemoryIcon
} from '@mui/icons-material';
import axios from '../api/axios';
import { useAuth } from '../context/AuthContext';

const SoftwareCatalog = () => {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [software, setSoftware] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [dialogOpen, setDialogOpen] = useState(false);
  const [versionDialogOpen, setVersionDialogOpen] = useState(false);
  const [selectedSoftware, setSelectedSoftware] = useState(null);
  const [typeFilter, setTypeFilter] = useState('all');
  const [formData, setFormData] = useState({
    name: '',
    type: 'operating_system',
    vendor: '',
    description: '',
    official_website: ''
  });
  const [versionFormData, setVersionFormData] = useState({
    version: '',
    release_date: '',
    end_of_support: '',
    is_current: false,
    notes: ''
  });

  useEffect(() => {
    fetchSoftware();
  }, [typeFilter]);

  const fetchSoftware = async () => {
    setLoading(true);
    try {
      const params = typeFilter !== 'all' ? { type: typeFilter } : {};
      const response = await axios.get('/software/catalog', { params });
      setSoftware(response.data);
    } catch (err) {
      setError('Ошибка загрузки каталога ПО');
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  const handleOpenDialog = (sw = null) => {
    if (sw) {
      setFormData({
        name: sw.name,
        type: sw.type,
        vendor: sw.vendor || '',
        description: sw.description || '',
        official_website: sw.official_website || ''
      });
      setSelectedSoftware(sw);
    } else {
      setFormData({
        name: '',
        type: 'operating_system',
        vendor: '',
        description: '',
        official_website: ''
      });
      setSelectedSoftware(null);
    }
    setDialogOpen(true);
  };

  const handleCloseDialog = () => {
    setDialogOpen(false);
    setSelectedSoftware(null);
  };

  const handleSave = async () => {
    try {
      if (selectedSoftware) {
        await axios.put(`/software/catalog/${selectedSoftware.id}`, formData);
      } else {
        await axios.post('/software/catalog', formData);
      }
      handleCloseDialog();
      fetchSoftware();
    } catch (err) {
      setError('Ошибка сохранения ПО');
      console.error(err);
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Удалить это ПО из каталога?')) return;

    try {
      await axios.delete(`/software/catalog/${id}`);
      fetchSoftware();
    } catch (err) {
      setError(err.response?.data?.detail || 'Ошибка удаления ПО');
    }
  };

  const handleOpenVersionDialog = (sw) => {
    setSelectedSoftware(sw);
    setVersionFormData({
      version: '',
      release_date: '',
      end_of_support: '',
      is_current: false,
      notes: ''
    });
    setVersionDialogOpen(true);
  };

  const handleSaveVersion = async () => {
    try {
      // Clean up empty date strings
      const cleanedData = {
        ...versionFormData,
        release_date: versionFormData.release_date || null,
        end_of_support: versionFormData.end_of_support || null
      };
      await axios.post(`/software/catalog/${selectedSoftware.id}/versions`, cleanedData);
      setVersionDialogOpen(false);
      fetchSoftware();
    } catch (err) {
      setError('Ошибка добавления версии');
      console.error(err);
    }
  };

  const getTypeIcon = (type) => {
    switch (type) {
      case 'operating_system':
        return <ComputerIcon />;
      case 'application':
        return <AppsIcon />;
      case 'firmware':
        return <MemoryIcon />;
      default:
        return <ComputerIcon />;
    }
  };

  const getTypeLabel = (type) => {
    switch (type) {
      case 'operating_system':
        return 'Операционная система';
      case 'application':
        return 'Приложение';
      case 'firmware':
        return 'Прошивка';
      default:
        return type;
    }
  };

  const canManage = user?.is_superuser || user?.permissions?.includes('software.manage');
  const canView = user?.is_superuser || user?.permissions?.includes('software.view');

  if (!canView) {
    return (
      <Box sx={{ p: 3 }}>
        <Alert severity="error">У вас нет прав для просмотра каталога ПО</Alert>
      </Box>
    );
  }

  return (
    <Box sx={{ p: 3 }}>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
        <Typography variant="h4">Каталог программного обеспечения</Typography>
        {canManage && (
          <Button
            variant="contained"
            startIcon={<AddIcon />}
            onClick={() => handleOpenDialog()}
          >
            Добавить ПО
          </Button>
        )}
      </Box>

      {error && <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError('')}>{error}</Alert>}

      <Tabs value={typeFilter} onChange={(e, value) => setTypeFilter(value)} sx={{ mb: 3 }}>
        <Tab label="Все" value="all" />
        <Tab label="Операционные системы" value="operating_system" icon={<ComputerIcon />} iconPosition="start" />
        <Tab label="Приложения" value="application" icon={<AppsIcon />} iconPosition="start" />
        <Tab label="Прошивки" value="firmware" icon={<MemoryIcon />} iconPosition="start" />
      </Tabs>

      {loading ? (
        <Typography>Загрузка...</Typography>
      ) : (
        <Grid container spacing={2}>
          {software.map((sw) => (
            <Grid item xs={12} key={sw.id}>
              <Card>
                <CardActionArea onClick={() => navigate(`/software-catalog/${sw.id}`)}>
                  <CardContent>
                    <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
                      <Box sx={{ display: 'flex', gap: 2, alignItems: 'center', flex: 1 }}>
                        {getTypeIcon(sw.type)}
                        <Box>
                          <Typography variant="h6">{sw.name}</Typography>
                          <Typography variant="body2" color="text.secondary">
                            {sw.vendor}
                          </Typography>
                          <Box sx={{ mt: 1, display: 'flex', gap: 1 }}>
                            <Chip
                              label={getTypeLabel(sw.type)}
                              size="small"
                              color="primary"
                              variant="outlined"
                            />
                            <Chip
                              label={`${sw.version_count || 0} версий`}
                              size="small"
                            />
                            <Chip
                              label={`Используется на ${sw.device_count || 0} устройствах`}
                              size="small"
                              color="secondary"
                            />
                          </Box>
                        </Box>
                      </Box>
                      {canManage && (
                        <Box onClick={(e) => e.stopPropagation()}>
                          <IconButton onClick={() => handleOpenDialog(sw)} size="small">
                            <EditIcon />
                          </IconButton>
                          <IconButton onClick={() => handleDelete(sw.id)} size="small" color="error">
                            <DeleteIcon />
                          </IconButton>
                          <Button
                            size="small"
                            onClick={() => handleOpenVersionDialog(sw)}
                            sx={{ ml: 1 }}
                          >
                            + Версия
                          </Button>
                        </Box>
                      )}
                    </Box>
                  </CardContent>
                </CardActionArea>
              </Card>
            </Grid>
          ))}
        </Grid>
      )}

      {/* Software Dialog */}
      <Dialog open={dialogOpen} onClose={handleCloseDialog} maxWidth="sm" fullWidth>
        <DialogTitle>{selectedSoftware ? 'Редактировать ПО' : 'Добавить ПО'}</DialogTitle>
        <DialogContent>
          <Box sx={{ pt: 2, display: 'flex', flexDirection: 'column', gap: 2 }}>
            <TextField
              label="Название"
              fullWidth
              required
              value={formData.name}
              onChange={(e) => setFormData({ ...formData, name: e.target.value })}
            />
            <TextField
              label="Тип"
              fullWidth
              required
              select
              value={formData.type}
              onChange={(e) => setFormData({ ...formData, type: e.target.value })}
            >
              <MenuItem value="operating_system">Операционная система</MenuItem>
              <MenuItem value="application">Приложение</MenuItem>
              <MenuItem value="firmware">Прошивка</MenuItem>
            </TextField>
            <TextField
              label="Производитель"
              fullWidth
              value={formData.vendor}
              onChange={(e) => setFormData({ ...formData, vendor: e.target.value })}
            />
            <TextField
              label="Описание"
              fullWidth
              multiline
              rows={3}
              value={formData.description}
              onChange={(e) => setFormData({ ...formData, description: e.target.value })}
            />
            <TextField
              label="Официальный сайт"
              fullWidth
              value={formData.official_website}
              onChange={(e) => setFormData({ ...formData, official_website: e.target.value })}
            />
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseDialog}>Отмена</Button>
          <Button onClick={handleSave} variant="contained">
            Сохранить
          </Button>
        </DialogActions>
      </Dialog>

      {/* Version Dialog */}
      <Dialog open={versionDialogOpen} onClose={() => setVersionDialogOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>Добавить версию</DialogTitle>
        <DialogContent>
          <Box sx={{ pt: 2, display: 'flex', flexDirection: 'column', gap: 2 }}>
            <TextField
              label="Версия"
              fullWidth
              required
              value={versionFormData.version}
              onChange={(e) => setVersionFormData({ ...versionFormData, version: e.target.value })}
            />
            <TextField
              label="Дата выпуска"
              fullWidth
              type="date"
              InputLabelProps={{ shrink: true }}
              value={versionFormData.release_date}
              onChange={(e) => setVersionFormData({ ...versionFormData, release_date: e.target.value })}
            />
            <TextField
              label="Окончание поддержки"
              fullWidth
              type="date"
              InputLabelProps={{ shrink: true }}
              value={versionFormData.end_of_support}
              onChange={(e) => setVersionFormData({ ...versionFormData, end_of_support: e.target.value })}
            />
            <TextField
              label="Заметки"
              fullWidth
              multiline
              rows={2}
              value={versionFormData.notes}
              onChange={(e) => setVersionFormData({ ...versionFormData, notes: e.target.value })}
            />
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setVersionDialogOpen(false)}>Отмена</Button>
          <Button onClick={handleSaveVersion} variant="contained">
            Добавить
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default SoftwareCatalog;
